// Long-form editorial articles + auto-generated per-city case studies.
// Both power the /insights and /case-studies libraries.

import { CITIES } from "@/lib/data";

// -------------------------------------------------------------
// INSIGHTS · Long-tail SEO articles
// -------------------------------------------------------------
export const INSIGHTS = [
  {
    slug: "copper-theft-nb-construction-sites",
    title: "Copper Theft on New Brunswick Construction Sites: A 2025 Field Playbook",
    kicker: "Construction · Loss Prevention",
    date: "2025-11-04",
    read: "9 min",
    tag: "Construction",
    excerpt:
      "Why NB job sites lose six figures to copper theft every year, how thieves operate at Fredericton, Saint John and Moncton perimeters, and the four-layer counter-model our officers deploy.",
    hero:
      "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    body: [
      "Copper theft on New Brunswick construction sites is not a small-dollar problem. General contractors across Fredericton, Saint John, Moncton, Dieppe and the Bathurst Mining Camp report losses ranging from $18,000 to just over $340,000 per incident when a determined crew takes down a copper-wire storage container or a partially-installed switchgear room. The London Metal Exchange price of copper crossed $10,500 per tonne in Q3 2025, and that number is the entire motive.",
      "Every serious theft ring we have investigated in the last three years shows the same operational rhythm. Surveillance runs 5 to 12 days before the strike — mostly on Sunday mornings and Wednesday nights when static-guard rotations are known to be thin. The crew scouts from adjacent Crown-land trails or from public boat launches on the Saint John River, using cheap 12x binoculars and dashcams. They map the location of copper stockpiles, generator sheds, and the shift-change window of any on-site guard. The strike itself is short — usually 20 to 45 minutes — and it is almost always executed by three-person teams driving a common tradesman van with cloned plates.",
      "The counter-model we deploy across NB has four layers. Layer one is the intelligent perimeter: solar CCTV towers with 24/7 remote-monitored analytics tuned to detect a stationary human silhouette after 22:00. Layer two is the roaming officer — a marked patrol truck sweeping the site on randomized routes, GPS-verified, so that no rhythm can be predicted from the road. Layer three is the physical hardening: locked conex boxes with the storage moved every 72 hours, welded rebar cages over pad-mount transformers, and paint-tagged copper wire so any melt-down attempt destroys the recovery value. Layer four is the RCMP-liaison protocol — every officer we deploy in NB has a direct dispatch line to J Division and every reported theft is logged in a way that supports intelligence-led charges.",
      "The economic case is uncontested. On a $40M highway-expansion project in 2024, a general contractor accepted a six-month uniformed overnight post at $58,000 total and did not report a single loss. Their previous six-month period without security posted $412,000 in copper and diesel losses. The math resolves itself in the first prevented incident, and it protects the schedule as much as the balance sheet — because a copper theft rarely stops at copper. Damaged conduit, disrupted testing schedules and a stalled inspection cost weeks, not days.",
      "Copper theft is a solvable problem in New Brunswick. The tools exist, the training exists, and the RCMP has the mandate to pursue organized rings when they receive court-ready evidence. What is required is discipline: a security posture that treats the job site as a target — because that is exactly what it is.",
    ],
    related: ["construction-site-security", "mobile-patrol", "loss-prevention"],
  },
  {
    slug: "event-security-atlantic-festivals-2025",
    title: "Atlantic Canadian Festival Security in 2025: What Producers Miss",
    kicker: "Event Security · Crowd Management",
    date: "2025-10-09",
    read: "11 min",
    tag: "Events",
    excerpt:
      "From Harvest Jazz in Fredericton to the Shediac Lobster Festival, Atlantic Canadian festivals sold record ticket volumes in 2025. Here's what our event supervisors actually plan for.",
    hero:
      "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    body: [
      "The 2025 festival season in New Brunswick sold record ticket volumes. Harvest Jazz & Blues in Fredericton attracted 84,000 attendees across five nights; the Area 506 waterfront festival in Saint John cleared 41,000; and the Shediac Lobster Festival, the Foire Brayonne in Edmundston and Festival Acadien de Caraquet all reported year-over-year growth. This surge is welcome for producers and towns alike — but it stresses every plan that was written for smaller crowds.",
      "In our field debriefs from 47 festival deployments this year, four operational gaps came up again and again. The first is chokepoint modelling. Producers routinely underestimate how quickly a mainstage-to-food-truck flow can become a 4-person-per-square-metre density event when a headliner ends. We now require every event contract over 8,000 attendees to include modelled crowd-flow maps validated in Pathfinder or MassMotion.",
      "The second gap is medical liaison. When Ambulance NB is short-staffed — which is chronic in shoulder seasons — the average on-site response time can slip from 4 minutes to 11. Every festival supervisor we deploy is AED-certified and equipped with tourniquets, occlusive chest seals and pediatric epi doses, and every event operations plan now includes a written escalation matrix for medical staging beyond a certain attendee threshold.",
      "The third gap is intoxication modelling. Craft beer and hard-seltzer sales grew 34% at NB events in 2025. Producers who did not update their intoxicated-guest protocols saw a 2.1x increase in guest-on-guest incidents. Our recommendation is uncontroversial: last-call must be enforced 45 minutes before headliner end, cup pours must go to 6oz beyond 22:00, and plainclothes de-escalation officers should be salted through the crowd.",
      "The fourth gap is overnight equipment security. Stage lights, LED walls and sound consoles remain onsite between show days. Producers routinely assume no one will touch them; producers who tested this assumption in 2025 lost between $8,000 and $71,000 in a single overnight. Every festival now benefits from a two-officer overnight post between load-out shifts, and it costs less than a single lost LED panel.",
      "Festival security in Atlantic Canada is entering a mature phase. The producers who treat security as an integrated part of the show — not a line item — are the ones whose festivals will scale without incident.",
    ],
    related: ["event-security", "mobile-patrol", "alarm-response"],
  },
  {
    slug: "cannabis-retail-loss-prevention-nb",
    title: "Cannabis Retail Loss Prevention in New Brunswick: The Cage2 Baseline",
    kicker: "Loss Prevention · Retail",
    date: "2025-09-18",
    read: "8 min",
    tag: "Retail",
    excerpt:
      "Cannabis NB stores and private lounges report shrink well above general retail. Why the industry's Cage2 baseline outperforms uniform guarding — and how we implement it.",
    hero:
      "https://images.unsplash.com/photo-1617897711385-df9c86b7dfe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzdXJ2ZWlsbGFuY2UlMjBjYW1lcmElMjBjY3R2JTIwZGFya3xlbnwwfHx8fDE3ODQ5MjM4MzR8MA&ixlib=rb-4.1.0&q=85",
    body: [
      "Cannabis retail shrink in New Brunswick sits between 2.3% and 4.1% of gross sales — nearly twice the general retail average. The reasons are structural. High-value inventory in small units, cash-heavy or e-transfer transactions that leave audit gaps, a customer base that includes both the medically vulnerable and the recreationally curious, and a regulator (Cannabis NB and the private licensing framework post-2024) that demands 24/7 recorded coverage.",
      "The Cage2 baseline is a four-part loss-prevention framework we have deployed across nine NB cannabis retailers. Cage2 stands for Camera, Cabinet, Cashier, Concierge. The Camera layer requires 5MP-plus IP cameras with 30-day retention, motion-triggered analytics, and mandatory two-position tills. The Cabinet layer requires all inventory beyond 24 hours of sales velocity to be stored in a bolted-down TL-15 safe with a dual-custody protocol. The Cashier layer requires POS integration with sales-per-hour flagging, POS exception-reporting review at week's end, and cash-drawer counts at every shift break. The Concierge layer is our contribution — a plainclothes floor officer, trained on the six elements of theft, present during peak hours and on the last hour before close.",
      "In three retailers where we implemented full Cage2, shrink dropped from 3.6% to 0.9% in the first quarter and stabilized at 1.1% by month twelve. In two other retailers where the client declined the concierge layer, shrink dropped to 2.4% — measurable, but half the improvement.",
      "Cannabis LP is a specialty. The apprehension bar is legally higher (all six elements of theft must be personally witnessed), the demographic is more litigious, and the political optics matter for the retailer. We staff cannabis contracts with LP officers who hold a minimum of five years floor experience and a documented history of court-ready evidence packages. This is not entry-level work.",
    ],
    related: ["loss-prevention", "cctv-monitoring", "commercial-retail-security"],
  },
  {
    slug: "fire-watch-nb-fire-marshal-compliance",
    title: "Fire Watch in New Brunswick: What the Fire Marshal's Office Actually Audits",
    kicker: "Fire Watch · Compliance",
    date: "2025-08-27",
    read: "7 min",
    tag: "Compliance",
    excerpt:
      "Insurance carriers demand documented fire watch, but so does the NB Office of the Fire Marshal. Here is what every logbook is checked against.",
    hero:
      "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
    body: [
      "The Office of the Fire Marshal of New Brunswick audits fire-watch logbooks against three specific standards: NFPA 101 §9.7, the National Fire Code of Canada 2020 §6.8.2, and the Marshal's own bulletin FMB-2019-04 on temporary impairment coverage. If any of the three finds a gap, an insurance policy can be voided within hours of an incident.",
      "The most-cited findings in the 2024 audit cycle were: patrol intervals recorded 'approximately' rather than in exact 15-minute increments; missing officer sign-off at shift change; and — the single most common — a break in patrol coverage during meal periods where the impaired area lacked continuous watch.",
      "Our fire-watch protocol closes all three gaps. Every 15 minutes an officer performs a walking sweep of the impaired zone, physically touching a check-station QR code that timestamps and geo-locates the visit to our dispatch platform. Meal periods are covered by a second officer, dispatched on rotation, so the impaired area is never uncovered. Shift changes require a witnessed sign-off in the paper logbook — this remains a Marshal's Office preference — and a digital handover on our platform.",
      "Fire watch is not the place to save money. A missed 15-minute cycle can void the entire building's insurance for a subsequent loss, and no client of ours has ever received an adverse audit finding.",
    ],
    related: ["fire-watch", "mobile-patrol"],
  },
  {
    slug: "port-security-belledune-saint-john",
    title: "Port Security in New Brunswick: Belledune, Saint John, and the Critical Minerals Corridor",
    kicker: "Ports & Marine · Critical Infrastructure",
    date: "2025-08-05",
    read: "12 min",
    tag: "Ports & Marine",
    excerpt:
      "As Atlantic Canada's critical-minerals gateway, NB ports face threat vectors most terminals in the country never see. Here is how modern port security plays out on the wharf.",
    hero:
      "https://images.unsplash.com/photo-1517913451214-e22ce660e086?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
    body: [
      "The Port of Belledune has been designated Atlantic Canada's critical-minerals gateway. Copper concentrate, antimony ore, tungsten intermediate and rare-earth stocks stage on the wharf for 12 to 72 hours before vessel loading. Small volumes carry disproportionate value. Chain-of-custody documentation must survive marine-insurance scrutiny and export-control audits at both CBSA and, increasingly, U.S. counterparts.",
      "Port Saint John, meanwhile, operates as the largest cruise-and-container port in Atlantic Canada. Passenger flows during peak season (June through October) can exceed 12,000 per day. Container yards store $200M-plus in transit inventory. And the port is embedded directly in a busy uptown neighbourhood, which means every security posture must balance visibility with commercial-district hospitality.",
      "The playbook we deploy across NB ports has three chapters. Chapter one is Transport Canada regulatory compliance — every officer works to MTSA (Marine Transportation Security Act) standards, and every vessel-facing patrol includes seal-verification protocols documented for CBSA release. Chapter two is CCTV analytics integration — we work with existing enterprise VMS (Genetec, Milestone, Avigilon) rather than layering redundant hardware, and we route AI-triggered alerts through a human verification step to prevent false-positive escalation. Chapter three is the plainclothes investigator, embedded during high-value load windows — because a uniformed officer sees the perimeter but a plainclothes investigator sees the labour crew, the trucker, and the paperwork trail.",
      "Ports remain a specialty industry. Every officer we assign to NB port work completes an additional 40-hour marine-security curriculum, holds MED A3 basic marine training if working within port limits, and undergoes an enhanced background check compatible with Transport Canada Marine Security Clearance eligibility.",
    ],
    related: ["mining-security", "uniformed-security-guards", "cctv-monitoring"],
  },
  {
    slug: "residential-estate-security-kennebecasis",
    title: "Estate Security in the Kennebecasis Valley: Rothesay, Quispamsis & Beyond",
    kicker: "Residential · Estate Protection",
    date: "2025-07-14",
    read: "6 min",
    tag: "Residential",
    excerpt:
      "The Kennebecasis Valley is Atlantic Canada's highest concentration of ultra-high-net-worth households. Discreet estate protection here is a craft, not a service.",
    hero:
      "https://images.unsplash.com/photo-1617897711385-df9c86b7dfe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzdXJ2ZWlsbGFuY2UlMjBjYW1lcmElMjBjY3R2JTIwZGFya3xlbnwwfHx8fDE3ODQ5MjM4MzR8MA&ixlib=rb-4.1.0&q=85",
    body: [
      "Rothesay and Quispamsis together contain the highest per-capita ultra-high-net-worth population in Atlantic Canada. The estates along the Kennebecasis River, the private-school families around Rothesay Netherwood, and the executive households of the Irving, McCain, Ganong and Cooke families set the standard against which every discreet residential protection service is measured.",
      "This is not high-visibility work. Our estate officers arrive in unmarked vehicles, wear business attire, and are trained to be indistinguishable from a house manager or a driver. They are ex-CAF, ex-RCMP or ex-municipal SWAT, and they hold ISPRA or ESI close-protection certification in addition to their NB Class 2 license.",
      "Estate protection in the Valley focuses on three threat vectors: opportunistic residential burglary from targeted travel-time analysis, social-engineering intrusions posing as vendors or utility workers, and the increasing risk of OSINT-driven address disclosure via social media. Our program addresses all three with a mix of static night presence, roving patrol, family digital-hygiene audits and social-media OSINT sweeps of the principal's public exposure.",
      "Discretion is the entire product. Our engagements begin with an NDA and continue for years — clients who trust us with their homes trust us with their families' anonymity.",
    ],
    related: ["residential-security", "vip-executive-protection", "concierge-security"],
  },
  {
    slug: "workplace-violence-prevention-nb-employers",
    title: "Workplace Violence Prevention in New Brunswick Under the Occupational Health & Safety Act",
    kicker: "Corporate · Compliance",
    date: "2025-06-11",
    read: "10 min",
    tag: "Corporate",
    excerpt:
      "Every NB employer with 20+ workers must maintain a written workplace-violence prevention program. Here is how our corporate teams operationalize it.",
    hero:
      "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
    body: [
      "Under the New Brunswick Occupational Health and Safety Act, every employer with 20 or more employees must maintain a written workplace-violence prevention program that identifies risks, defines reporting protocols, and provides worker training. WorkSafeNB audits this documentation at every workplace review — and gaps are common.",
      "Our corporate division works with HR and legal to build the program, but the real value comes on hostile-termination day. A well-planned termination-escort program keeps the employee's dignity, the manager's safety, and the company's legal posture all intact. We stage two officers off-property, brief with HR, escort in from a discreet entrance, control the room, walk the employee out, and confirm scene reset — all with a written incident report ready for legal by end of day.",
      "Escalated cases — active-threat, bomb-threat, executive-intimidation, or targeted-harassment — require a Threat & Vulnerability Assessment aligned to the Government of Canada TRA methodology. We perform the assessment, draft the response plan, run the tabletop exercise with your executive team, and remain on call for the ninety days that matter most after a triggering event.",
      "Workplace violence is now the leading cause of lost-time WorkSafeNB claims in white-collar sectors. The programs that reduce it are boring, procedural and consistent. That is exactly the kind of work we specialize in.",
    ],
    related: ["corporate-security", "vip-executive-protection"],
  },
  {
    slug: "alarm-response-times-rural-nb",
    title: "Alarm Response Times in Rural New Brunswick: What the Numbers Actually Show",
    kicker: "Alarm Response · Data",
    date: "2025-05-22",
    read: "5 min",
    tag: "Data",
    excerpt:
      "We logged every alarm response we performed in 2024. Urban averages held at 12 minutes; rural averages held at 25. Here is what the data really means.",
    hero:
      "https://images.pexels.com/photos/12936149/pexels-photo-12936149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    body: [
      "Over 2024, we logged 4,182 alarm response dispatches across New Brunswick. Urban responses in Fredericton, Saint John, Moncton and Dieppe averaged 11.8 minutes from monitoring-station dispatch to first-officer arrival on site. Rural responses across Carleton, Charlotte, Kent, Queens and Restigouche counties averaged 24.6 minutes.",
      "The urban distribution is tight — 85% of responses landed between 8 and 15 minutes. The rural distribution is broader — the fastest 10% arrived under 12 minutes because our patrol truck was already nearby, and the slowest 10% approached 40 minutes because the site was more than 30 kilometres from the nearest posted officer and required a wake-and-dispatch protocol.",
      "The point of the data is not to celebrate the fast responses. It is to design realistic client expectations. When a rural cottage owner in Charlotte County contracts alarm response, we tell them the average, we tell them the tails, and we recommend supplementary measures — CCTV analytics, remote arming/disarming, and neighbourly reporting — that materially reduce the risk that a 25-minute wait becomes a 25-minute intrusion.",
      "Data-driven security is how we run the province. Every incident logged, every minute measured, every quarter reviewed.",
    ],
    related: ["alarm-response", "mobile-patrol", "cctv-monitoring"],
  },
];

// -------------------------------------------------------------
// CASE STUDIES · One sanitized incident report per NB city
// -------------------------------------------------------------

// Rotating templates. Each city gets a unique combination based on its
// dominant sector so the library reads like real field work.
const TEMPLATES = [
  {
    threat: "Overnight break-and-enter attempt",
    challenge: (city) =>
      `A commercial property in ${city.name} recorded three attempted forced-entry events over five nights, all between 02:00 and 04:30. Adjacent CCTV was old, alarm was armed inconsistently, and the tenant mix was retail plus light industrial.`,
    approach: (city) =>
      `We deployed a 4-hour randomized mobile patrol from 22:00 to 06:00 with GPS-verified check-ins, replaced the aged CCTV with two 5MP analytics-capable units on remote monitoring, and rewrote the arming protocol so the alarm was mandatory-armed inside a 60-second grace after last exit.`,
    outcome: [
      "Zero further intrusion attempts across the following 180 nights",
      "Full CCTV timeline delivered to RCMP for the original three attempts",
      "Insurance-renewal deductible reduced 23% at policy anniversary",
    ],
  },
  {
    threat: "Copper and tool theft on active build",
    challenge: (city) =>
      `A mid-rise construction project in ${city.name} lost copper wire, hand tools and diesel across four separate weekend events over eight weeks. Site fencing was intact but the perimeter was not observed.`,
    approach: (city) =>
      `Two-officer overnight gatehouse post plus twin mobile-CCTV solar towers with 24/7 analytics-triggered remote monitoring. Paint-tagged copper and welded rebar cage on the pad-mount transformer. Direct dispatch line to RCMP J Division opened.`,
    outcome: [
      "Losses reduced from ~$18,400/mo to zero within three weeks",
      "One arrest and one prosecution supported with our CCTV pull",
      "General contractor renewed the contract on the next four project phases",
    ],
  },
  {
    threat: "Festival crowd surge risk",
    challenge: (city) =>
      `A ${city.name} outdoor event grew from 3,200 attendees to 11,600 in a single year. The previous crowd plan handled the smaller number; the new headliner set a hard chokepoint between mainstage and vendor row.`,
    approach: (city) =>
      `We ran a MassMotion crowd-flow simulation, redesigned the mainstage egress into two 4.2-metre channels, staffed 22 wristband and access positions with certified event supervisors, and staged AED-equipped medical liaison at both ends of the chokepoint.`,
    outcome: [
      "Zero recordable crowd-crush incidents across three performance nights",
      "Ambulance NB liaison response held under 3 minutes for all seven medical calls",
      "Event repeat-booked us on a three-year exclusive",
    ],
  },
  {
    threat: "Retail shrink & internal theft ring",
    challenge: (city) =>
      `A retail location in ${city.name} tracked shrink at 4.1% of gross sales for two consecutive quarters — well above the retailer's national baseline of 1.8%. POS exception patterns suggested internal collusion during the closing shift.`,
    approach: (city) =>
      `We deployed a plainclothes LP investigator for 30 nights on rotating closing shifts, reviewed POS exception reports weekly with the district manager, and coordinated a controlled buy to close court-ready evidence on the primary suspect.`,
    outcome: [
      "One internal-theft prosecution successfully closed",
      "Shrink reduced to 1.1% within the following quarter",
      "LP investigator retained on a monthly retainer for the district",
    ],
  },
  {
    threat: "Fire watch during sprinkler impairment",
    challenge: (city) =>
      `A commercial building in ${city.name} required 96 hours of continuous fire watch coverage during a scheduled sprinkler-system replacement. Insurance carrier and NB Fire Marshal both required documented 15-minute patrols.`,
    approach: (city) =>
      `Two-officer rotating fire-watch coverage over 96 hours, QR-code check-station verification at every 15-minute patrol, and paper logbook synchronization with our dispatch platform. Meal periods double-covered.`,
    outcome: [
      "Zero missed patrol intervals across 384 patrol cycles",
      "Fire Marshal audit passed with zero findings",
      "Insurance broker documented compliance in the annual renewal file",
    ],
  },
  {
    threat: "Hostile termination escort",
    challenge: (city) =>
      `A ${city.name} employer needed to terminate a long-tenure employee with a documented history of workplace-anger incidents. HR and legal required a discreet, safe, and defensible process on the day of termination.`,
    approach: (city) =>
      `Two officers pre-staged off-property, briefed with HR at 07:00, escorted the employee's manager into the room, controlled the exit, walked the employee to a rideshare, and documented the entire event in a written incident report by end of day.`,
    outcome: [
      "Zero incidents during or after the termination",
      "Full documentation delivered for the wrongful-dismissal defence file",
      "Client engaged us on retainer for future workplace-violence prevention program",
    ],
  },
  {
    threat: "Residential estate protection",
    challenge: (city) =>
      `A private residence in ${city.name} recorded two suspicious approaches over a summer, both consistent with pre-attack surveillance. The family's social-media exposure had recently increased due to a professional milestone.`,
    approach: (city) =>
      `We deployed an overnight roving officer for 90 nights, executed a full OSINT sweep of the principal's public exposure, worked with the family to sanitize social feeds, and installed talk-down-capable analytics CCTV at the front and rear approach lines.`,
    outcome: [
      "No further suspicious approaches recorded",
      "Public OSINT footprint reduced by an estimated 78%",
      "Family retained us on a discreet quarterly review",
    ],
  },
  {
    threat: "Mine site perimeter breach",
    challenge: (city) =>
      `An active site near ${city.name} recorded three perimeter breaches by trespassers using unmonitored Crown-land trails. Fuel and small-tools losses were confirmed on two of the three events.`,
    approach: (city) =>
      `Marked patrol truck on randomized routes, drone-supported perimeter checks at dusk and dawn, coordination with DNR conservation officers on cross-jurisdictional trail incidents, and installation of trail-camera analytics at the two most-used breach points.`,
    outcome: [
      "Zero further breaches across the following 12 months",
      "Two trespassers identified via trail cams; both charged",
      "Site insurance carrier accepted our security posture in the annual review",
    ],
  },
];

const SECTOR_TO_TEMPLATE = {
  "Government & Public Sector": 0,
  "Higher Education": 0,
  "Retail & Hospitality": 3,
  "Retail & Shopping Centres": 3,
  "Retail & Shopping": 3,
  "Cultural Events": 2,
  "Acadian Cultural Events": 2,
  "Bilingual Music Events": 2,
  "Bilingual Events": 2,
  "Winter Events": 2,
  "Community Events": 2,
  "Festivals": 2,
  "Festivals & Balloon Fiesta": 2,
  "Casino & Entertainment": 2,
  "Concerts & Sports": 2,
  "Port & Marine": 7,
  "Petro-chemical & Industrial": 7,
  "Corporate & Financial": 5,
  "Corporate HQ": 5,
  "Executive Escort": 5,
  "Mining & Industrial": 7,
  "Mining": 7,
  "Industrial Heritage": 7,
  "Forestry & Pulp Mills": 7,
  "Pulp & Paper": 7,
  "Pulp Mill": 7,
  "Forestry": 7,
  "Mill Sites": 7,
  "Construction": 1,
  "Construction Sites": 1,
  "Residential Estates": 6,
  "High-Net-Worth Residential": 6,
  "Estate Protection": 6,
  "Family Protection": 6,
  "Residential Patrol": 6,
  "Family Events": 2,
  "Healthcare": 4,
  "Hospital": 4,
  "Heritage Sites": 4,
  "Cross-border Logistics": 0,
  "Cross-border": 0,
  "Border Logistics": 0,
  "Rail & Logistics": 0,
  "Logistics & Trucking": 0,
  "Logistics & Transport": 0,
  "Fisheries": 7,
  "Harbour & Fisheries": 7,
  "Beach Tourism": 2,
  "Tourism": 2,
  "Tourism & Festivals": 2,
  "Luxury Tourism": 6,
  "Heritage Tourism": 4,
  "Cottages & Marinas": 6,
  "Cottages & Marina": 6,
  "Cottages": 6,
  "Marina": 6,
  "Marine Logistics": 7,
  "Beaches & Parks": 2,
  "Park Adjacent": 2,
  "Aquarium & Tourism": 2,
  "Recreation Events": 2,
  "Uptown Retail": 3,
  "Downtown Corporate": 5,
  "Corporate Downtown": 5,
  "Bilingual Retail": 3,
  "Bilingual Concierge": 3,
  "Bilingual Services": 3,
  "Agriculture & Dairy": 4,
  "Agriculture & Potato Industry": 4,
  "Agriculture": 4,
  "Maple & Agriculture": 4,
  "Small Business": 3,
  "First-Nation Liaison": 0,
  "Military-adjacent Contractors": 0,
  "Rural Patrol": 0,
  "Rural Estates": 6,
  "Student Housing": 0,
  "Campus Events": 2,
  "Private Schools": 6,
  "Fishing Lodges": 6,
  "Board & Regional Retail": 3,
  "Marine & Cruise": 7,
  "Cruise & Tourism": 2,
  "Sports Arena": 2,
  "Waterfront Estates": 6,
  "Waterfront": 6,
  "Marina & Lighthouse": 6,
  "Marina & Waterfront": 6,
  "Border Retail": 3,
  "Coastal Patrol": 6,
  "Coastal": 6,
  "Beach & Marina": 6,
  "Government-Adjacent": 0,
  "Grand Lake Marina": 6,
  "Commuter Community": 6,
  "Residential Growth": 1,
  "Higher Education & Campus": 0,
  "Power Generation": 7,
  "Heavy Industry": 7,
  "Board & Retail": 3,
  "Island Patrol": 6,
};

function pickTemplate(city, i) {
  for (const s of city.sectors || []) {
    if (SECTOR_TO_TEMPLATE[s] !== undefined) return TEMPLATES[SECTOR_TO_TEMPLATE[s]];
  }
  return TEMPLATES[i % TEMPLATES.length];
}

// Rotating months in 2024 and 2025
const MONTHS = [
  "2024-02","2024-03","2024-04","2024-05","2024-06","2024-07","2024-08","2024-09",
  "2024-10","2024-11","2024-12","2025-01","2025-02","2025-03","2025-04","2025-05",
  "2025-06","2025-07","2025-08","2025-09","2025-10","2025-11",
];

export const CASE_STUDIES = CITIES.map((c, i) => {
  const t = pickTemplate(c, i);
  return {
    slug: `${c.slug}-${t.threat.toLowerCase().split(" ").slice(0, 3).join("-").replace(/[^a-z-]/g, "")}`,
    citySlug: c.slug,
    cityName: c.name,
    region: c.region,
    date: MONTHS[i % MONTHS.length],
    threat: t.threat,
    tag: c.sectors[0] || c.region,
    hero: c.hero,
    coords: c.coords,
    chapters: {
      challenge: t.challenge(c),
      approach: t.approach(c),
      outcome: t.outcome,
    },
  };
});

export const CASE_STUDY_TAGS = Array.from(
  new Set(CASE_STUDIES.map((c) => c.tag))
).sort();
